import React from 'react';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Grid from '@mui/material/Grid';
import {
  TextV2,
  SvgArrowLeft1Regular,
} from '@insights-ltd/design-library/components';
import { useNavigate, Link as RouterLink } from 'react-router';
import { IJob } from 'types/job';
import {
  TRANSLATION_JOB_DIALECTS as DIALECTS,
  getAvailableStatusTransitions,
  EnumJobStatus,
  JobStatus,
} from 'variables';
import { dateFromISOToLocal } from 'utils/utils';
import { JobButton } from 'components/Jobs';
import { getExportUrl } from 'api';
import { JobsStatus } from 'components/Jobs/Table/JobsStatus';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import {
  JobDetailField,
  JobDetailDescription,
  JobFieldLabel,
} from './JobFields';

type JobDetailsProps = {
  job: IJob;
  enableExport?: boolean;
  onImport: (file: File) => void;
  publishJob: () => void;
  openDialog: () => void;
};

const capitalise = (str: string | undefined): string => {
  if (!str) return '';

  const words = str.split('_');
  return words
    .map((s) => `${s[0].toUpperCase()}${s.substring(1).toLowerCase()}`)
    .join(' ');
};

const disabledStatuses: JobStatus[] = [
  EnumJobStatus.CANCELLED,
  EnumJobStatus.PUBLISHED,
];

const JobDetails = ({
  job,
  enableExport = false,
  onImport,
  publishJob,
  openDialog,
}: JobDetailsProps) => {
  const navigate = useNavigate();

  const deadline = job.deadlineDate
    ? dateFromISOToLocal(job.deadlineDate)
    : undefined;

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    onImport(e.target.files[0]);
  };

  const onClickEdit = () => navigate(`/jobs/${job.id}/edit`);

  const exportVariant = (
    [EnumJobStatus.IN_REVIEW, EnumJobStatus.PENDING_PUBLISH] as JobStatus[]
  ).includes(job.status)
    ? 'outlined'
    : 'contained';

  const disabledStatus = disabledStatuses.includes(job.status);
  const ableToChangeStatus =
    getAvailableStatusTransitions(job.status).length > 0;
  return (
    <Grid container spacing={2}>
      <Grid
        container
        size={12}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '64px',
        }}
      >
        <TextV2 variant="h3">Details</TextV2>
        {!disabledStatus && (
          <JobButton
            sx={{
              whiteSpace: 'nowrap',
            }}
            onClick={onClickEdit}
            variant="outlined"
            color="primary"
          >
            Edit job details
          </JobButton>
        )}
      </Grid>
      <Grid size={12}>
        <Card
          sx={(theme) => ({
            padding: theme.spacing(spacingSizeMap.XS),
            border: '0',
          })}
          elevation={0}
        >
          <CardContent>
            <Grid container spacing={1}>
              <Grid size={12}>
                <TextV2 variant="h4">{DIALECTS[job.dialect]}</TextV2>
              </Grid>
              <Grid
                container
                sx={{
                  alignItems: 'center',
                }}
              >
                <JobsStatus jobStatus={job.status} />
                {ableToChangeStatus && (
                  <JobButton
                    onClick={openDialog}
                    aria-label="Change job status"
                  >
                    Change
                  </JobButton>
                )}
              </Grid>
            </Grid>
            <Grid
              container
              sx={(theme) => ({
                marginTop: theme.spacing(spacingSizeMap.S),
              })}
            >
              <JobDetailField>
                <JobFieldLabel text="Name" />
                <JobDetailDescription>
                  <TextV2>{job.title}</TextV2>
                </JobDetailDescription>
              </JobDetailField>

              <JobDetailField>
                <JobFieldLabel text="Deadline" />
                <JobDetailDescription>
                  <TextV2 variant="h4">{deadline}</TextV2>
                </JobDetailDescription>
              </JobDetailField>

              <JobDetailField>
                <JobFieldLabel text="Owner" />
                <JobDetailDescription>
                  <TextV2>{job.owner}</TextV2>
                </JobDetailDescription>
              </JobDetailField>

              <JobDetailField>
                <JobFieldLabel text="Requester" />
                <JobDetailDescription>
                  <TextV2>{job.requester}</TextV2>
                </JobDetailDescription>
              </JobDetailField>

              <JobDetailField>
                <JobFieldLabel text="Translator" />
                <JobDetailDescription>
                  <TextV2>{capitalise(job.translator)}</TextV2>
                </JobDetailDescription>
              </JobDetailField>

              <Grid
                container
                spacing={1}
                sx={(theme) => ({
                  marginTop: theme.spacing(spacingSizeMap.XS),
                })}
              >
                <Grid size={12}>
                  <JobFieldLabel text="Description" />
                </Grid>
                <Grid size={12}>
                  <TextV2>{job.description}</TextV2>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>

          <CardActions>
            {enableExport && (
              <JobButton
                variant={exportVariant}
                color="primary"
                href={getExportUrl(job.id)}
                target="_blank"
                download
                fullWidth
              >
                Export
              </JobButton>
            )}
            {job.status === EnumJobStatus.IN_REVIEW && (
              <JobButton
                variant="contained"
                color="primary"
                component="label"
                fullWidth
              >
                Import
                <input
                  accept=".csv"
                  hidden
                  type="file"
                  aria-label="Import"
                  onChange={onFileChange}
                />
              </JobButton>
            )}
            {job.status === EnumJobStatus.PENDING_PUBLISH && (
              <JobButton
                variant="contained"
                color="primary"
                onClick={publishJob}
                fullWidth
              >
                Publish
              </JobButton>
            )}
          </CardActions>
        </Card>
      </Grid>
      <Grid>
        <Link component={RouterLink} to="/jobs">
          <Grid
            container
            sx={{
              alignItems: 'center',
            }}
          >
            <SvgArrowLeft1Regular /> Back to jobs
          </Grid>
        </Link>
      </Grid>
    </Grid>
  );
};

export default JobDetails;
